import { blogSubCategories } from 'domains/models/blog_post';
import {
  ProductType,
  getProductTypeByTags,
} from 'domains/models/blog_post/productType';
import { translate } from 'infras/i18n';
import { useRouter } from 'next/router';
import Cookie from 'js-cookie';
import {
  ResourceItem,
  ResourceListItem,
  ResourceTagItem,
} from 'presentations/api/schema/resources';
import { ServicePortalContext } from 'presentations/reducers/ServicePortal';
import {
  apiPath,
  resourceCategoryPath,
  resourceListPath,
  resourcePath,
  webPath,
} from 'presentations/routes/path';
import { setBreadcrumbs } from 'presentations/views/helpers/breadcrumbs';
import { useRequest } from 'presentations/views/hooks/request';
import {
  SyntheticEvent,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import searchInsights from 'search-insights';
import { setEvent } from 'presentations/views/helpers/analytics';
import { useThrottle } from 'presentations/views/components/atoms/Calendar/hooks/useThrottle';
import { getOptions } from 'presentations/views/hooks/track';
import { getDateTime } from 'infras/datetime';
import { modalImageAdId } from 'presentations/views/components/molecules/OwnedContentBanner';
import { isMediaMax } from 'presentations/views/styles';

export interface Props {
  resource: ResourceItem;
  category: string;
  populars: ResourceListItem[];
}

export const useResourcePage = (props: Props) => {
  const { state: serviceState } = useContext(ServicePortalContext);
  const [isShowAdStory, setIsShowAdStory] = useState<boolean>(false);
  const [isShowScreenBanner, setIsShowScreenBanner] = useState<boolean>(false);
  const [isDisableShowScreenBanner, setIsDisableShowScreenBanner] = useState<
    boolean
  >(false);
  const isMobile = isMediaMax('tablet');
  const viewer = serviceState.viewer;
  const cookieKey = 'mg_ad';

  useEffect(() => {
    const isDisable = Cookie.get(cookieKey);
    if (isDisable) {
      setIsDisableShowScreenBanner(true);
    }
    const random = Math.floor(Math.random() * 2);
    setIsShowAdStory(random === 0);
  }, []);

  const onScroll = useThrottle((e: any) => {
    const scrollLockY = document.body.scrollHeight / 2;
    if (scrollLockY < window.scrollY) {
      setIsShowScreenBanner(true);
    }
    if (isShowScreenBanner) {
      window.scrollY = scrollLockY;
    }
  }, 500);

  useEffect(() => {
    if (!isShowScreenBanner) return;
    setEvent(
      'show blog post mobile banner',
      {
        category: props.resource.categories.map((c) => c.slug),
        tag: props.resource.tags.map((t) => t.slug),
        slug: props.resource.slug,
        title: props.resource.title,
        contentId: modalImageAdId,
      },
      { isAmplitude: false },
    );
  }, [isShowScreenBanner]);

  useEffect(() => {
    if (isShowScreenBanner) {
      window.removeEventListener('scroll', onScroll);
      return;
    }
    window.addEventListener('scroll', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
    };
  }, [isShowScreenBanner]);

  useEffect(() => {
    if (!serviceState.isInitialized) {
      return;
    }
    if (
      !process.env['ALGOLIA_APPLICATION_ID'] ||
      !process.env['ALGOLIA_SEARCH_ONLY_BLOG_SCOPED_API_KEY']
    ) {
      return;
    }
    searchInsights('init', {
      appId: process.env['ALGOLIA_APPLICATION_ID'],
      apiKey: process.env['ALGOLIA_SEARCH_ONLY_BLOG_SCOPED_API_KEY'],
    });
    const userToken = viewer ? viewer.uid : `anonymous-${Cookie.get('uuid')}`;
    searchInsights('clickedObjectIDs', {
      userToken,
      index: 'BlogPost',
      eventName: 'Click BlogPost',
      objectIDs: [String(props.resource.id)],
    });
    searchInsights('convertedObjectIDs', {
      userToken,
      index: 'BlogPost',
      eventName: 'Read BlogPost',
      objectIDs: [String(props.resource.id)],
    });
    searchInsights('viewedObjectIDs', {
      userToken,
      index: 'BlogPost',
      eventName: 'View Popular List',
      objectIDs: props.populars.map((r) => String(r.id)),
    });
    searchInsights('viewedObjectIDs', {
      userToken,
      index: 'BlogPost',
      eventName: 'View Related List',
      objectIDs: props.resource.relatedPosts.map((r) => String(r.id)),
    });
    setEvent(
      'read blog post',
      {
        category: props.resource.categories.map((c) => c.slug),
        tag: props.resource.tags.map((t) => t.slug),
        slug: props.resource.slug,
        title: props.resource.title,
      },
      { isAmplitude: false },
    );
  }, [serviceState.isInitialized, props.resource.id]);

  const onCloseScreenBanner = () => {
    setIsShowScreenBanner(false);
    setIsDisableShowScreenBanner(true);
    const options = getOptions();
    options.expires = getDateTime().plus({ day: 1 }).toJSDate();
    Cookie.set(cookieKey, '1', options);
  };

  const tagsRequest = useRequest(apiPath(`/v1/resources/tags`), {
    revalidateOnFocus: false,
  });
  const tags: ResourceTagItem[] = tagsRequest.data || [];

  const breadcrumbs = [];
  breadcrumbs.push({
    title: translate('breadcrumbs.Resource'),
    link: resourceListPath(),
  });
  if (blogSubCategories.includes(props.category)) {
    breadcrumbs.push({
      title: translate(`resource.category.blog`),
      link: resourceCategoryPath({ category: 'blog' }),
    });
  }
  // breadcrumbs.push({
  //   title: categoryName,
  //   link: resourceCategoryPath({ category: props.category }),
  // });
  breadcrumbs.push({
    title: props.resource.title,
    link: resourcePath(props.category, props.resource.slug),
  });
  const routes = setBreadcrumbs(breadcrumbs);

  const router = useRouter();
  const shareUrl = webPath(
    resourcePath(props.resource.categories[0].slug, props.resource.slug),
    router.locale,
  );
  const productType = useMemo(() => {
    const tags = props.resource.tags.map((t) => t.slug);
    return getProductTypeByTags(tags);
  }, [props.resource.tags]);

  const onClickStory = (e: SyntheticEvent<HTMLAnchorElement>) => {
    setEvent('click resource banner', {
      position: 'sub_top',
      contentType: 'story',
    });
  };

  return {
    state: {
      viewer,
      routes,
      shareUrl,
      productType,
      tags,
      isShowScreenBanner,
      isDisableShowScreenBanner,
      isShowAdStory,
      isMobile,
    },
    actions: {
      onCloseScreenBanner,
      onClickStory,
    },
  };
};
